import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API } from '../../backend';
import authHeader from '../../services/auth-header';

function cleanJson(raw) {
  return raw
    .replace(/\/\/[^\n]*/g, '')        // strip // comments
    .replace(/\/\*[\s\S]*?\*\//g, '')  // strip /* */ comments
    .replace(/,(\s*[}\]])/g, '$1');    // remove trailing commas
}

export default function ContractJsonImport() {
  const [jsonInput, setJsonInput]     = useState('');
  const [loading, setLoading]         = useState(false);
  const [result, setResult]           = useState(null);
  const [importError, setImportError] = useState('');

  const handleClear = () => {
    setJsonInput('');
    setResult(null);
    setImportError('');
  };

  const handleSave = async () => {
    setResult(null);
    setImportError('');

    if (!jsonInput.trim()) {
      setImportError('Please paste JSON data before saving.');
      return;
    }

    const cleaned = cleanJson(jsonInput);

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}company/bulk-import/`,
        cleaned,
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      setResult(res.data);
      const created = (res.data.created || []).length;
      const skipped = (res.data.skipped || []).length;
      if (skipped === 0) {
        toast.success(`Import complete — ${created} contract(s) created.`);
      } else {
        toast.warning(`Import finished — ${created} created, ${skipped} failed.`);
      }
    } catch (err) {
      // axios throws on 4xx/5xx — but the response body still has skipped details
      const data = err?.response?.data;
      if (data && (data.skipped || data.created)) {
        // partial or full failure with structured response — show results panel
        setResult(data);
        const skipped = (data.skipped || []).length;
        toast.error(`Import failed — ${skipped} contract(s) could not be processed.`);
      } else {
        const detail =
          data?.error  ||
          data?.detail ||
          data?.message ||
          err?.message  ||
          'Import request failed.';
        setImportError(detail);
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const createdList = result?.created || [];
  const skippedList = result?.skipped || [];

  return (
    <div className="container-fluid py-4">

      {/* Header */}
      <div className="header pb-0 mb-3">
        <h5 className="tc">Contract JSON Import</h5>
        <h6>
          <Link to="/settings">Settings</Link> / Contract JSON Import
        </h6>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <p className="text-muted small mb-3">
            Paste a JSON object with a <code>funded_transactions</code> array to bulk-import
            contracts, parties, assets and payment schedules in one request.
          </p>

          {/* Textarea */}
          <textarea
            className={`form-control font-monospace${importError && !result ? ' is-invalid' : ''}`}
            rows={28}
            spellCheck={false}
            style={{ fontSize: '13px', resize: 'vertical', whiteSpace: 'pre' }}
            placeholder={'{\n  "funded_transactions": [\n    { ... }\n  ]\n}'}
            value={jsonInput}
            onChange={(e) => {
              setJsonInput(e.target.value);
              setImportError('');
              setResult(null);
            }}
          />

          {/* top-level request error (no structured response) */}
          {importError && (
            <div className="alert alert-danger d-flex align-items-start gap-2 mt-2 mb-0 py-2">
              <i className="fa fa-exclamation-circle mt-1 flex-shrink-0" />
              <span style={{ fontSize: '13px' }}>{importError}</span>
            </div>
          )}

          {/* Buttons */}
          <div className="d-flex gap-2 justify-content-end mt-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleClear}
              disabled={loading}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={loading}
            >
              {loading
                ? <><span className="spinner-border spinner-border-sm me-1" />Importing…</>
                : 'Save'
              }
            </button>
          </div>

          {/* Results */}
          {result && (
            <div className="mt-4">

              {/* summary bar */}
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="fw-semibold small">Import Results</span>
                {createdList.length > 0 && (
                  <span className="badge bg-success">{createdList.length} created</span>
                )}
                {skippedList.length > 0 && (
                  <span className="badge bg-danger">{skippedList.length} failed</span>
                )}
                {result.message && (
                  <span className="text-muted small ms-1">{result.message}</span>
                )}
              </div>

              {/* created contracts */}
              {createdList.length > 0 && (
                <div className="row g-2 mb-3">
                  {createdList.map((item, i) => (
                    <div key={i} className="col-md-6 col-lg-4">
                      <div className="border border-success rounded p-3 h-100">
                        <p className="fw-semibold text-success small mb-2">
                          <i className="fa fa-check-circle me-1" />
                          {item.contract_number || `Contract #${i + 1}`}
                        </p>
                        <table className="table table-sm table-borderless mb-0" style={{ fontSize: '12px' }}>
                          <tbody>
                            {item.parties_created != null && (
                              <tr>
                                <td className="text-muted py-0 pe-2">Parties created</td>
                                <td className="py-0">{item.parties_created}</td>
                              </tr>
                            )}
                            {item.parties_reused != null && (
                              <tr>
                                <td className="text-muted py-0 pe-2">Parties reused</td>
                                <td className="py-0">{item.parties_reused}</td>
                              </tr>
                            )}
                            {item.assets_created != null && (
                              <tr>
                                <td className="text-muted py-0 pe-2">Assets</td>
                                <td className="py-0">{item.assets_created}</td>
                              </tr>
                            )}
                            {item.PMT != null && (
                              <tr>
                                <td className="text-muted py-0 pe-2">PMT</td>
                                <td className="py-0">
                                  ${Number(item.PMT).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                              </tr>
                            )}
                            {item.interest_only?.interest_only_type && (
                              <tr>
                                <td className="text-muted py-0 pe-2">Interest-only</td>
                                <td className="py-0">{item.interest_only.interest_only_type}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* skipped / failed */}
              {skippedList.length > 0 && (
                <div className="d-flex flex-column gap-2">
                  {skippedList.map((entry, i) => (
                    <div key={i} className="border border-danger rounded p-3">
                      <p className="fw-semibold text-danger small mb-1">
                        <i className="fa fa-times-circle me-1" />
                        {entry.contract_number
                          ? `Contract "${entry.contract_number}" failed`
                          : `Transaction #${i + 1} failed`}
                        {entry.reason && (
                          <span className="fw-normal text-muted ms-2">— {entry.reason}</span>
                        )}
                      </p>
                      {(entry.errors || []).length > 0 && (
                        <ul className="mb-0 ps-3" style={{ fontSize: '12px' }}>
                          {entry.errors.map((e, j) => (
                            <li key={j} className="text-danger">
                              <span className="text-muted">[{e.section}]</span>{' '}
                              <strong>{e.field}</strong>: {e.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
